import { useState, useEffect } from 'react';
import { brandsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Tag, Plus, Edit2, Trash2, Loader2, Hash } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const derivePrefix = (name) =>
    (name || '').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase();

const emptyBrand = { name: '', prefix: '', logo_url: '' };

const Brands = () => {
    const { isAdmin } = useAuth();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyBrand);
    const [prefixTouched, setPrefixTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const { data } = await brandsAPI.getAll();
            setBrands(data);
        } catch {
            toast.error('Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyBrand);
        setPrefixTouched(false);
        setOpen(true);
    };

    const openEdit = (b) => {
        setEditingId(b.id);
        setForm({ name: b.name, prefix: b.prefix, logo_url: b.logo_url || '' });
        setPrefixTouched(true);
        setOpen(true);
    };

    const handleNameChange = (value) => {
        const next = { ...form, name: value };
        if (!prefixTouched) {
            next.prefix = derivePrefix(value);
        }
        setForm(next);
    };

    const handlePrefixChange = (value) => {
        setPrefixTouched(true);
        setForm({
            ...form,
            prefix: value.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase(),
        });
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Brand name is required');
            return;
        }
        if (form.prefix.length !== 3) {
            toast.error('Prefix must be exactly 3 letters');
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await brandsAPI.update(editingId, { name: form.name, prefix: form.prefix, logo_url: form.logo_url });
                toast.success('Brand updated');
            } else {
                await brandsAPI.create({ name: form.name, prefix: form.prefix, logo_url: form.logo_url });
                toast.success('Brand created');
            }
            setOpen(false);
            fetchBrands();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (b) => {
        if (!window.confirm(`Delete brand "${b.name}"? This cannot be undone.`)) return;
        try {
            await brandsAPI.delete(b.id);
            toast.success('Brand deleted');
            fetchBrands();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delete failed');
        }
    };

    return (
        <div data-testid="brands-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                        Master Data
                    </p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Brands</h1>
                    <p className="text-gray-500 mt-1">
                        Define your brands once. Products you add under a brand get an auto SKU like{' '}
                        <span className="font-mono text-[#1A1A1A]">ORSX001</span>.
                    </p>
                </div>
                <Button
                    onClick={openCreate}
                    data-testid="new-brand-btn"
                    className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                >
                    <Plus size={16} className="mr-2" /> Add Brand
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Tag className="text-[#C08478]" size={20} />
                        All Brands ({brands.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="animate-spin text-[#C08478]" />
                        </div>
                    ) : brands.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            No brands yet. Click <span className="font-medium">Add Brand</span>.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F5F5F5]">
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Prefix</TableHead>
                                    <TableHead className="text-right">Products Issued</TableHead>
                                    <TableHead>Next SKU</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {brands.map((b) => (
                                    <TableRow key={b.id} data-testid={`brand-row-${b.id}`}>
                                        <TableCell className="font-medium">{b.name}</TableCell>
                                        <TableCell>
                                            <Badge className="bg-[#1A1A1A] text-white font-mono text-[11px]">
                                                {b.prefix}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                            {b.sequence_count}
                                        </TableCell>
                                        <TableCell className="font-mono text-sm text-[#C08478]">
                                            <Hash size={12} className="inline mr-1" />
                                            {b.next_sku_preview}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEdit(b)}
                                                data-testid={`edit-brand-${b.id}`}
                                                className="text-gray-500 hover:text-[#C08478]"
                                            >
                                                <Edit2 size={16} />
                                            </Button>
                                            {isAdmin() && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(b)}
                                                    data-testid={`delete-brand-${b.id}`}
                                                    className="text-gray-500 hover:text-red-500"
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="brand-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Brand' : 'New Brand'}</DialogTitle>
                        <DialogDescription>
                            Brands have a 3-letter prefix used to auto-generate product SKUs.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Brand Name *</Label>
                            <Input
                                data-testid="brand-name"
                                value={form.name}
                                onChange={(e) => handleNameChange(e.target.value)}
                                placeholder="e.g. ORS, Maybelline, L'Oréal..."
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>
                                SKU Prefix (3 letters)
                                {!prefixTouched && form.name && (
                                    <span className="text-xs text-gray-400 ml-2 font-normal">
                                        (auto from name)
                                    </span>
                                )}
                            </Label>
                            <Input
                                data-testid="brand-prefix"
                                value={form.prefix}
                                onChange={(e) => handlePrefixChange(e.target.value)}
                                maxLength={3}
                                placeholder="ORS"
                                className="font-mono uppercase tracking-widest"
                            />
                            {form.prefix.length === 3 && (
                                <p className="text-xs text-gray-500">
                                    Next product SKU will be{' '}
                                    <span className="font-mono font-bold text-[#C08478]">
                                        {form.prefix}X001
                                    </span>
                                </p>
                            )}
                        </div>
                        <div className="space-y-2 col-span-2">
                            <Label>Brand Logo</Label>
                            <ImageUploader
                                value={form.logo_url}
                                onChange={(url) => setForm({ ...form, logo_url: url })}
                                kind="brand"
                                placeholder="https://…/logo.png"
                                data-testid="brand-logo"
                            />
                            <p className="text-xs text-gray-400">Optional — used on catalog headers and PDFs.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="save-brand"
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                        >
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editingId ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Brands;
